/**
 * Holdings List Component
 * Displays detailed list of portfolio holdings with performance data
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PortfolioHolding, PortfolioConfig } from '../types/portfolio';
import { Card } from './Card';
import { getAssetTypeColor } from '@/utils/assetTypeConfig';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/utils/formatPrice';
import { getAssetCurrency } from '@/utils/assetCurrency';
import { currencyConverter } from '@/services/currencyConverter';

interface HoldingsListProps {
  holdings: PortfolioHolding[];
  config: PortfolioConfig;
  onEditHolding?: (holding: PortfolioHolding) => void;
  onRemoveHolding?: (holdingId: string) => void;
}

type SortField = 'symbol' | 'value' | 'pnl' | 'pnlPercent' | 'weight';
type SortDirection = 'asc' | 'desc';

export const HoldingsList: React.FC<HoldingsListProps> = ({ 
  holdings, 
  config
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>('value');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const formatCurrency = (value: number) => {
    const symbol = config.baseCurrency === 'USD' ? '$' : config.baseCurrency;
    return `${symbol}${formatPrice(value)}`;
  };

  const formatPriceWithCurrency = (price: number, assetCurrency: string, baseCurrency: string) => {
    const assetCurrencyUpper = assetCurrency.toUpperCase();
    const baseCurrencyUpper = baseCurrency.toUpperCase();
    
    // If same currency, just show the price
    if (assetCurrencyUpper === baseCurrencyUpper) {
      return formatCurrency(price);
    }

    // Get currency symbol
    const getCurrencySymbol = (currency: string) => {
      switch (currency.toUpperCase()) {
        case 'USD': return '$';
        case 'HKD': return 'HK$';
        case 'CNY': return '¥';
        default: return currency;
      }
    };

    const assetSymbol = getCurrencySymbol(assetCurrency);
    const originalPrice = `${assetSymbol}${formatPrice(price)}`;
    
    // Try to get converted price from cache
    const rate = currencyConverter.getCachedRate(assetCurrency, baseCurrency);
    if (rate !== null) {
      const convertedPrice = price * rate;
      return `${originalPrice} (${formatCurrency(convertedPrice)})`;
    }

    // If no rate available, just show original
    return originalPrice;
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    let aValue: number;
    let bValue: number;

    switch (sortField) {
      case 'symbol':
        return sortDirection === 'asc' 
          ? a.asset.symbol.localeCompare(b.asset.symbol)
          : b.asset.symbol.localeCompare(a.asset.symbol);
      case 'value':
        aValue = a.currentValue;
        bValue = b.currentValue;
        break;
      case 'pnl':
        aValue = a.unrealizedPnL;
        bValue = b.unrealizedPnL;
        break;
      case 'pnlPercent':
        aValue = a.unrealizedPnLPercent;
        bValue = b.unrealizedPnLPercent;
        break;
      case 'weight':
        aValue = a.weight;
        bValue = b.weight;
        break;
      default:
        return 0;
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('portfolio.holdings')} ({holdings.length})
        </h3>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th 
                className="text-left py-3 px-2 font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('symbol')}
              >
                <div className="flex items-center gap-2">
                  {t('portfolio.asset')}
                  {getSortIcon('symbol')}
                </div>
              </th>
              <th className="text-right py-3 px-2 font-medium text-gray-500 dark:text-gray-400">
                {t('portfolio.quantity')}
              </th>
              <th 
                className="text-right py-3 px-2 font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('value')}
              >
                <div className="flex items-center justify-end gap-2">
                  {t('portfolio.currentValue')}
                  {getSortIcon('value')}
                </div>
              </th>
              <th 
                className="text-right py-3 px-2 font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('pnl')}
              >
                <div className="flex items-center justify-end gap-2">
                  {t('portfolio.pnl')}
                  {getSortIcon('pnl')}
                </div>
              </th>
              <th 
                className="text-right py-3 px-2 font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort('weight')}
              >
                <div className="flex items-center justify-end gap-2">
                  {t('portfolio.weight')}
                  {getSortIcon('weight')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings.map((holding, index) => (
              <motion.tr
                key={holding.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <td className="py-4 px-2">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-900 dark:text-white text-base">
                      {holding.asset.name}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAssetTypeColor(holding.asset.type)}`}>
                      {holding.asset.type.charAt(0).toUpperCase() + holding.asset.type.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {holding.asset.symbol}
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <div className="font-semibold text-gray-900 dark:text-white text-base">
                    {holding.quantity.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    @ {formatPriceWithCurrency(
                      holding.averageCost,
                      getAssetCurrency(holding.asset),
                      config.baseCurrency
                    )}
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <div className="font-semibold text-gray-900 dark:text-white text-base">
                    {formatCurrency(holding.currentValue)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatPriceWithCurrency(
                      holding.asset.price || holding.averageCost,
                      getAssetCurrency(holding.asset),
                      config.baseCurrency
                    )} {t('portfolio.each')}
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <div className={`font-bold text-base ${getChangeColor(holding.unrealizedPnL)}`}>
                    {formatCurrency(holding.unrealizedPnL)}
                  </div>
                  {config.showPercentages && (
                    <div className={`text-sm font-medium ${getChangeColor(holding.unrealizedPnL)}`}>
                      {formatPercentage(holding.unrealizedPnLPercent)}
                    </div>
                  )}
                </td>
                <td className="py-4 px-2 text-right">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {holding.weight.toFixed(1)}%
                  </div>
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1 ml-auto mt-1">
                    <div
                      className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(holding.weight, 100)}%` }}
                    />
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {sortedHoldings.map((holding, index) => (
          <motion.div
            key={holding.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-gray-900 dark:text-white text-lg">
                    {holding.asset.name}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAssetTypeColor(holding.asset.type)}`}>
                    {holding.asset.type.charAt(0).toUpperCase() + holding.asset.type.slice(1)}
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {holding.asset.symbol}
                </div>
              </div>
            </div>

            {/* Key Metrics - Prominent Display */}
            <div className="space-y-3 mb-3">
              {/* Quantity */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('portfolio.quantity')}</span>
                <div className="text-right">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {holding.quantity.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    @ {formatPriceWithCurrency(
                      holding.averageCost,
                      getAssetCurrency(holding.asset),
                      config.baseCurrency
                    )}
                  </div>
                </div>
              </div>

              {/* Current Value */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('portfolio.currentValue')}</span>
                <div className="text-right">
                  <div className="font-bold text-gray-900 dark:text-white text-lg">
                    {formatCurrency(holding.currentValue)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatPriceWithCurrency(
                      holding.asset.price || holding.averageCost,
                      getAssetCurrency(holding.asset),
                      config.baseCurrency
                    )} {t('portfolio.each')}
                  </div>
                </div>
              </div>

              {/* P&L */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('portfolio.pnl')}</span>
                <div className="text-right">
                  <div className={`font-bold text-lg ${getChangeColor(holding.unrealizedPnL)}`}>
                    {formatCurrency(holding.unrealizedPnL)}
                  </div>
                  {config.showPercentages && (
                    <div className={`text-sm font-medium ${getChangeColor(holding.unrealizedPnL)}`}>
                      {formatPercentage(holding.unrealizedPnLPercent)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Weight */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-500 dark:text-gray-400">{t('portfolio.portfolioWeight')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {holding.weight.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(holding.weight, 100)}%` }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
};